import { useMemo, useState } from 'react';

import { useGetDashboardDataByWorkspace } from '@/api/dashboard/useDashboardDetailsByWorkspace';
import { ActivityLog } from '@/models/ActivityLogsByWorkspace';
import { MemberOverview } from '@/models/AudienceByWorkspace';
import { ChurnSignal } from '@/models/DashboardData';
import { SegmentMembership } from '@/models/Segments';

import ChurnSignalModal from '../../../DashboardPage/Components/ChurnSignalModal';
import { RecommendedActionExplanationModal } from '../../../DashboardPage/Components/RecommendedActionExplanationModal';

import ChurnRiskSection from './ChurnRiskSection';
import MemberActivityVolumeSection from './MemberActivityVolumeSection';
import { extractChurnSignalsFromDashboard, filterSignalsForMember } from './memberProfileChurnUtils';
import RecentActivitySection from './RecentActivitySection';
import RecommendedActionsSection from './RecommendedActionsSection';

interface OverviewPageProps {
  memberData: MemberOverview | undefined;
  /** Logs for this profile, matched by email (no customer user id on their platform). */
  activityLogs?: ActivityLog[];
  changeActiveStep: (step: string) => void;
  segmentsData: SegmentMembership | undefined;
  /** Translated Activities tab label (must match TabMenu) for “View all” on the chart. */
  activitiesTabLabel: string;
}

const OverviewPage: React.FC<OverviewPageProps> = ({
  memberData,
  activityLogs,
  changeActiveStep,
  activitiesTabLabel,
}) => {
  const [selectedSignal, setSelectedSignal] = useState<ChurnSignal | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [memberOnlyActionExplanation, setMemberOnlyActionExplanation] = useState<string | null>(null);

  // Calculate date range for the last 30 days to fetch signals
  const { startTime, endTime } = useMemo(() => {
    const now = new Date();
    const endDate = new Date(now);
    const startDate = new Date(now);
    startDate.setDate(now.getDate() - 30);

    // Use fixed times to prevent constant changes
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    return {
      startTime: startDate.toISOString(),
      endTime: endDate.toISOString(),
    };
  }, []);

  // Fetch dashboard data to get signals
  const { data: dashboardData } = useGetDashboardDataByWorkspace(startTime, endTime);

  const memberSignals = useMemo(() => {
    const all = extractChurnSignalsFromDashboard(dashboardData);
    return filterSignalsForMember(all, memberData?.id, memberData?.email);
  }, [dashboardData, memberData?.id, memberData?.email]);

  // Extract and combine recommended actions from multiple sources
  const allRecommendedActions = useMemo(() => {
    const actionsSet = new Set<string>();

    // 1. Add actions from memberData.recommendedActions
    if (Array.isArray(memberData?.recommendedActions)) {
      memberData?.recommendedActions.forEach((action) => {
        if (typeof action === 'string' && action.trim()) {
          actionsSet.add(action.trim());
        }
      });
    }

    // 2. Add actions from parsedChurnRiskData.recommendedActions
    const parsedChurnRiskActions = memberData?.parsedChurnRiskData?.recommendedActions;
    if (Array.isArray(parsedChurnRiskActions)) {
      parsedChurnRiskActions.forEach((action) => {
        if (typeof action === 'string' && action.trim()) {
          actionsSet.add(action.trim());
        }
      });
    }

    // 3. Add actions from signals for this member
    memberSignals.forEach((signal) => {
      if (Array.isArray(signal.recommendedActions)) {
        signal.recommendedActions.forEach((action) => {
          if (typeof action === 'string' && action.trim()) {
            actionsSet.add(action.trim());
          }
        });
      }
    });

    return Array.from(actionsSet);
  }, [memberData, memberSignals]);

  const recommendedActionsEnrichmentHint = useMemo(() => {
    const parsed = memberData?.parsedChurnRiskData;
    if (!parsed?.recommendedActions?.length) return 'none' as const;
    const hasReason = parsed.recommendedActionsV2?.some((a) => a.reason?.trim());
    if (parsed.recommendedActionsLlmEnrichedAt && hasReason) return 'ai_enriched' as const;
    return 'deterministic_only' as const;
  }, [memberData?.parsedChurnRiskData]);

  // Create a map of actions to signals for quick lookup
  const actionToSignalMap = useMemo(() => {
    const map = new Map<string, ChurnSignal>();

    // For each signal, map its actions to the signal
    // If multiple signals have the same action, prioritize by:
    // 1. Most recent (by createdAt)
    // 2. Highest priority
    memberSignals.forEach((signal) => {
      if (Array.isArray(signal.recommendedActions)) {
        signal.recommendedActions.forEach((action) => {
          if (typeof action === 'string' && action.trim()) {
            const trimmedAction = action.trim();
            const existingSignal = map.get(trimmedAction);

            if (!existingSignal) {
              map.set(trimmedAction, signal);
            } else {
              // Compare signals - prefer more recent or higher priority
              const existingDate = new Date(existingSignal.createdAt).getTime();
              const currentDate = new Date(signal.createdAt).getTime();

              const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
              const existingPriority = priorityOrder[existingSignal.priority as keyof typeof priorityOrder] || 0;
              const currentPriority = priorityOrder[signal.priority as keyof typeof priorityOrder] || 0;

              // Prefer more recent, or if same date, prefer higher priority
              if (currentDate > existingDate || (currentDate === existingDate && currentPriority > existingPriority)) {
                map.set(trimmedAction, signal);
              }
            }
          }
        });
      }
    });

    return map;
  }, [memberSignals]);

  const handleActionClick = (action: string) => {
    const trimmed = action.trim();
    const signal = actionToSignalMap.get(trimmed);

    if (signal) {
      setSelectedSignal(signal);
      setIsModalOpen(true);
      return;
    }

    if (memberData) {
      setMemberOnlyActionExplanation(trimmed);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSignal(null);
  };

  const handleCloseMemberActionExplanation = () => {
    setMemberOnlyActionExplanation(null);
  };

  return (
    <div>
      <div className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,4.5fr)_minmax(0,4.5fr)] lg:items-stretch">
        <div className="flex min-h-0 h-full min-w-0 flex-col">
          <RecommendedActionsSection
            actions={allRecommendedActions}
            riskScore={memberData?.churnRiskAnalysis?.riskScore ?? memberData?.parsedChurnRiskData?.riskScore ?? 0}
            onActionClick={handleActionClick}
            enrichmentHint={recommendedActionsEnrichmentHint}
          />
        </div>

        <div className="flex min-h-0 h-full min-w-0 flex-col">
          <ChurnRiskSection
            memberData={memberData}
            churnData={memberData?.parsedChurnRiskData}
            churnRiskAnalysis={memberData?.churnRiskAnalysis}
            changeActiveStep={changeActiveStep}
          />
        </div>

        <div className="flex min-h-0 h-full min-w-0 w-full flex-col">
          <MemberActivityVolumeSection
            activities={activityLogs ?? memberData?.activityLogs}
            changeActiveStep={changeActiveStep}
            activitiesTabLabel={activitiesTabLabel}
            compact
          />
        </div>
      </div>

      {/* Full-width Recent Activity section */}
      <RecentActivitySection
        activities={activityLogs ?? memberData?.activityLogs}
        changeActiveStep={changeActiveStep}
      />

      {/* Signal Modal */}
      <ChurnSignalModal isOpen={isModalOpen} onClose={handleCloseModal} signal={selectedSignal} />

      {memberOnlyActionExplanation && memberData && (
        <RecommendedActionExplanationModal
          isOpen
          onClose={handleCloseMemberActionExplanation}
          action={memberOnlyActionExplanation}
          context={{ type: 'member', member: memberData }}
          onSuccessfulTicketCreate={handleCloseMemberActionExplanation}
        />
      )}
    </div>
  );
};

export default OverviewPage;
